import { useEffect } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import Shell from 'components/Shell/Shell';
import StyleContextProvider from 'utils/context/StyleContextProvider';
import { getMasterControl, getBlogs } from '@viome/vnxt-nucleus';
import ErrorPage from 'pages/404';
import BlogHead from 'components/Blog/BlogHead';
import BlogArticles from 'components/Blog/BlogArticles';
import {
    BLOG_ARTICLES_STEP_LIMIT,
    contentfulOptions as options,
    VLS_BLOG_CATEGORIES,
} from 'utils/constants';
import styles from '../blog.module.scss';

const Blogs = (props) => {
    const { preview, masterControl, blogHeader, page, blogs } = props,
        title = blogHeader?.title,
        blogTitle = blogs?.title,
        subText = blogTitle ?? blogHeader?.subtext,
        blogIndexData = page?.content,
        categories = blogIndexData?.topicsCollection.items;

    if (!page) {
        return <ErrorPage />;
    }
    if (blogTitle === null) {
        const router = useRouter();
        useEffect(() => {
            router.replace('/blog');
        });
    }

    return (
        <Shell
            masterControl={masterControl}
            page={page}
            preview={preview}
            className="blog viome viome3"
            isLegacy={false}
        >
            <StyleContextProvider styles={styles}>
                <BlogHead
                    categories={categories}
                    title={title}
                    subText={subText}
                    category={{
                        slug: blogs.slug,
                        title: blogs.title,
                    }}
                    isTop
                />
                <section
                    className={classNames(
                        styles.blog__Fold,
                        styles.category__Fold,
                    )}
                >
                    <BlogArticles
                        blogArticles={blogs}
                        limit={BLOG_ARTICLES_STEP_LIMIT}
                        skip={0}
                        preview={preview}
                        infinite
                        slug={blogs.slug}
                    />
                </section>
            </StyleContextProvider>
        </Shell>
    );
};

export default Blogs;

export async function getStaticProps({ params, preview = false }) {
    const { slug } = params;

    if (slug && !VLS_BLOG_CATEGORIES.includes(slug)) {
        return {
            notFound: true,
        };
    }

    const initialBlogsAmount = BLOG_ARTICLES_STEP_LIMIT,
        updateMetaData = (page, title = '') => {
            const parsedPage = page;

            if (parsedPage) {
                const { metaTitle, metaDescription, canonicalUrl } =
                    page.metaData;

                if (title === 'Most Recent') {
                    parsedPage.metaData.metaTitle = 'Most Recent Blog Articles';
                    parsedPage.metaData.metaDescription =
                        'Most Recent Blog Articles';
                    parsedPage.metaData.canonicalUrl =
                        'https://www.viome.com/blog/category/most-recent';
                } else {
                    parsedPage.metaData.metaTitle = metaTitle.replace(
                        '[category]',
                        title,
                    );
                    parsedPage.metaData.metaDescription =
                        metaDescription.replace('[category]', title);
                    parsedPage.metaData.canonicalUrl = canonicalUrl.replace(
                        '[slug]',
                        slug,
                    );
                }
            }
            return parsedPage || null;
        },
        categoryRouter = (slug) => {
            if (slug === 'most-recent') {
                return getBlogs.getBlogsByDateDESC(options, initialBlogsAmount);
            }
            return getBlogs.getBlogsByTopic(slug, options, initialBlogsAmount);
        },
        pageData = Promise.all([
            getMasterControl.get(options),
            getBlogs.getBlogHeader(options),
            getBlogs.getBlogIndex(options),
            categoryRouter(slug),
        ]),
        props = await pageData.then(
            async ([masterControl, blogHeader, page, blogs]) => ({
                preview,
                masterControl: masterControl || null,
                blogHeader: blogHeader || null,
                // We need to update the static SEO metadata with dynamic values
                page: updateMetaData(page, blogs.title),
                blogs,
            }),
        );

    return { props, revalidate: 3600 };
}

export async function getStaticPaths() {
    const allCategories = await getBlogs.getCategoriesSelectedSlugs(
        { preview: false },
        VLS_BLOG_CATEGORIES,
    );

    return {
        paths: Array.isArray(allCategories)
            ? allCategories.map((category) => {
                  const slug = category?.slug;
                  return `/blog/category/${slug}` ?? [];
              })
            : [],
        fallback: 'blocking',
    };
}
